import { useReducedMotion } from 'motion/react';
import { Fragment, type ReactNode } from 'react';

/** Delay before the first word, then 55 ms per word (NEWS-08). */
const FIRST_WORD_MS = 120;
export const WORD_STAGGER_MS = 55;

/** `{name}` at the start of a word, optionally followed by punctuation. */
const SLOT = /^\{(\w+)\}(.*)$/;

/**
 * A title revealed word by word, each word rising out of its own mask
 * (NEWS-08); already in place under reduced motion (NEWS-09).
 *
 * `slots` put nodes in the text: "A {chip} ganhou 15 novidades." with
 * `slots={{ chip: <StoreChip /> }}` reveals the chip as one word. With an
 * i18n text, pass the slot's own token as the param
 * (`LL.….title({ chip: '{chip}' })`) so each locale keeps its word order.
 */
export function WordReveal({
  text,
  slots = {},
  startIndex = 0,
}: {
  text: string;
  slots?: Record<string, ReactNode>;
  startIndex?: number;
}) {
  const reduceMotion = useReducedMotion() ?? false;
  const words = text.split(' ').filter((word) => word.length > 0);

  return words.map((word, i) => {
    const match = SLOT.exec(word);
    const slot = match ? slots[match[1]] : undefined;
    const content =
      match && slot !== undefined ? (
        <>
          {slot}
          {match[2]}
        </>
      ) : (
        word
      );
    return (
      <Fragment key={i}>
        {i > 0 && ' '}
        <span
          data-testid="word-mask"
          className="-mb-[0.1em] inline-block overflow-hidden pb-[0.1em] align-top"
        >
          <span
            className={
              reduceMotion ? 'inline-block' : 'inline-block animate-word-up'
            }
            style={
              reduceMotion
                ? undefined
                : {
                    animationDelay: `${FIRST_WORD_MS + (startIndex + i) * WORD_STAGGER_MS}ms`,
                  }
            }
          >
            {content}
          </span>
        </span>
      </Fragment>
    );
  });
}
